<script>
export default {
  name: 'TwSearchableSelect',
  props: {
    value: {
      model: true,
      type: [ String, Number, Object, Array ],
      default: null,
    },
    items: {
      type: Array,
      default: () => [],
    },
    itemText: {
      type: [ String, Function ],
      default: 'text',
    },
    itemValue: {
      type: [ String, Function ],
      default: 'value',
    },
    label: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: 'Search...',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    allowCreate: {
      type: Boolean,
      default: false,
    },
    createText: {
      type: String,
      default: 'Create',
    },
  },
  data() {
    return {
      searchQuery: '',
      isOpen: false,
      menuProps: {
        closeOnContentClick: false,
        closeOnClick: false,
        auto: true,
        offsetY: true,
      },
    };
  },
  computed: {
    filteredItems() {
      if (!this.searchQuery) return this.items;

      const query = this.searchQuery.trim().toLowerCase();
      if (!query) return this.items;

      return this.items.filter(item => {
        const text = this.getItemText(item);
        if (!text) return false;

        // Handle both string and object items
        const searchText = String(text).toLowerCase();
        return searchText.includes(query);
      });
    },
    showCreateOption() {
      return this.allowCreate &&
        this.searchQuery &&
        this.searchQuery.trim() &&
        this.filteredItems.length === 0;
    },
    displayItems() {
      const items = [ ...this.filteredItems ];

      if (this.showCreateOption) {
        const searchText = this.searchQuery.trim();
        items.push({
          isCreateOption: true,
          text: `${this.createText} "${searchText}"`,
          value: searchText,
        });
      }

      return items;
    },
  },
  methods: {
    getItemText(item) {
      if (!item) return '';

      try {
        if (typeof this.itemText === 'function') {
          return this.itemText(item);
        }
        return item[this.itemText] || '';
      } catch (error) {
        console.warn('Error getting item text:', error);
        return '';
      }
    },
    getItemValue(item) {
      if (!item) return null;

      try {
        if (typeof this.itemValue === 'function') {
          return this.itemValue(item);
        }
        return item[this.itemValue] || null;
      } catch (error) {
        console.warn('Error getting item value:', error);
        return null;
      }
    },
    onItemSelect(item) {
      if (item.isCreateOption) {
        this.$emit('create', item.value);
      } else {
        const value = this.getItemValue(item);
        this.$emit('input', this.multiple ? [ ...this.value, value ] : value);
      }
      this.isOpen = false;
      this.searchQuery = '';
      this.$refs.select && this.$refs.select.blur();
    },
    onSearchInput(value) {
      this.searchQuery = value || '';
      // Keep menu open when typing
      this.isOpen = true;
    },
    onMenuToggle(isOpen) {
      if (this.isOpen !== isOpen) {
        this.isOpen = isOpen;
        if (!isOpen) {
          this.searchQuery = '';
        }
      }
    },
  },
};
</script>

<template>
  <v-select
    ref="select"
    v-model="value"
    :items="displayItems"
    :item-text="itemText"
    :item-value="itemValue"
    :label="label"
    :placeholder="placeholder"
    :disabled="disabled"
    :multiple="multiple"
    :menu-props="menuProps"
    class="tw-searchable-select"
    @update:menu="onMenuToggle"
  >
    <!-- Search input -->
    <template #prepend-item>
      <v-text-field
        v-model="searchQuery"
        :placeholder="placeholder"
        hide-details
        dense
        clearable
        class="px-2"
        @input="onSearchInput"
        @click.stop
        @keydown.stop
      />
      <v-divider />
    </template>

    <!-- Custom item template -->
    <template #item="{ item, on, attrs }">
      <v-list-tile
        v-bind="attrs"
        :class="{ 'create-option': item.isCreateOption }"
        v-on="on"
        @click.stop="onItemSelect(item)"
      >
        <v-list-tile-avatar v-if="item.isCreateOption">
          <v-icon color="primary">mdi-plus</v-icon>
        </v-list-tile-avatar>
        <v-list-tile-content>
          <v-list-tile-title>
            {{ item.isCreateOption ? item.text : getItemText(item) }}
          </v-list-tile-title>
        </v-list-tile-content>
      </v-list-tile>
    </template>
  </v-select>
</template>

<style scoped>
.tw-searchable-select {
  width: 100%;
}

.create-option {
  border-left: 3px solid var(--v-primary-base);
  background-color: var(--v-grey-lighten4);
}

.create-option .v-list-tile__title {
  color: var(--v-primary-base);
  font-weight: 500;
}

.theme--dark .create-option {
  background-color: var(--v-grey-darken3);
}
</style>



// WEBPACK FOOTER //
// src/components/TwSearchableSelect.vue